import { load } from 'cheerio';
import _ from "i18n";
import { v4 as uuidv4 } from 'uuid';
import { TEMPLATE_TYPES } from "constants";

import {
  findRowLayoutType,
  indexLayoutDynamicFields,
  findRowColumnCount,
  removeDynamicFieldsIndexes,
} from "./rowParser";
import {
  escapeJsonSpecialChars,
  ALL_LAYOUT_STRING_CODES,
  EMPTY_COLUMN_EXAMPLE,
  UNREFERENCED_BLOCK_CODES,
  FAKE_FEEDS,
  CHANNEL_FIELDS,
  EXPERT_FIELDS,
  CHANNEL_FORMATION_FIELDS,
} from "./helpers";
import moment from "moment";
import { store } from "../../index.js";

const ROW_CONTAINER_CLASSES = [".email-row-container", ".u-row-container"];

export const decomposeNewsletter = (template, callback) => {
  let design = JSON.parse(template.design);

  const $ = load(template.content);
  const rowsContainers = $(ROW_CONTAINER_CLASSES.join(","));

  let skeleton = (design.body.rows || []).reduce((acc, rowDesign, index) => {
    const rowHtml = $(rowsContainers[index] || "").html() || "";
    const rowType = findRowLayoutType(rowHtml);
    const column = findRowColumnCount(rowHtml, rowType);

    acc.push({
      id: uuidv4(),
      type: rowType,
      column,
      design: indexLayoutDynamicFields(JSON.stringify(rowDesign), column),
      content: indexLayoutDynamicFields(rowHtml, column),
      feeds: [],
    });

    return acc;
  }, []);

  callback(skeleton);
};

export const replaceContentFeed = (
  content,
  feeds = [],
  escapeJsonChars = false,
) => {
  const tagsIndexed = feeds?.length > 0;
  if (!tagsIndexed) {
    feeds = [
      Object.keys(FAKE_FEEDS).reduce((acc, feedType) => {
        acc = { ...acc, ...FAKE_FEEDS[feedType] };
        return acc;
      }, []),
    ];
  }

  return ALL_LAYOUT_STRING_CODES.reduce((acc, e) => {
    feeds.forEach((feed, index) => {
      let property =
        !feed[e.property] ||
        (Array.isArray(feed[e.property]) && feed[e.property].length === 0)
          ? ""
          : feed[e.property];
      const value = escapeJsonChars
        ? escapeJsonSpecialChars(property)
        : feed[e.property];
      acc = acc.replace(
        new RegExp(tagsIndexed ? `${e.key}__${index}` : e.key, "g"),
        value,
      );
    });

    return acc;
  }, content + "");
};

const getNextNewsletterNum = (num) => {
  if (!num && num !== 0 && num !== "0") return null;
  const parts = String(num).replace("#", "").split("-");
  const campaignPart = parts[parts.length - 1];
  if (!/^\d+$/.test(campaignPart)) return null;
  return parseInt(campaignPart, 10) + 1;
};

const CHANNEL_CODES_REGEX = /CHANNEL_|EXPERT_/;

/**
 * Replace the placeholders of a collection (experts, replay formations).
 * `cursor` keeps, per code, how many occurrences were already consumed so
 * that a collection can be spread over several blocks: the 1st occurrence
 * of EXPERT_NAME in the newsletter gets experts[0], the 2nd experts[1], ...
 * Codes are consumed in declaration order, so a code that is the prefix of
 * another (EXPERT_URL / EXPERT_URL_LABEL) must be declared last.
 */
const replaceCollectionFields = (content, fields, collection, cursor, render) =>
  fields.reduce((acc, { key, property, fallback }) => {
    let position = cursor[key] || 0;

    const replaced = acc.replace(new RegExp(key, "g"), () =>
      render(collection[position++]?.[property], fallback),
    );

    cursor[key] = position;
    return replaced;
  }, content);

export const replaceChannelFields = (
  content = "",
  escapeJsonChars = false,
  cursor = {},
) => {
  const text = content + "";
  if (!CHANNEL_CODES_REGEX.test(text)) {
    return text;
  }

  // The channel is null until it has been resolved from the newsletter
  // articles, and stays null for campaigns that have no channel at all.
  const channel = store.getState().currentCampaign?.uaNewsletter?.channel || {};

  const render = (raw, fallback = "") => {
    const value = raw === undefined || raw === null ? fallback : String(raw);
    return escapeJsonChars ? escapeJsonSpecialChars(value) : value;
  };

  // A function replacement keeps `$&`, `$1`, ... in the channel data literal.
  const withChannel = CHANNEL_FIELDS.reduce(
    (acc, { key, property }) =>
      acc.replace(new RegExp(key, "g"), () => render(channel[property])),
    text,
  );

  const withExperts = replaceCollectionFields(
    withChannel,
    EXPERT_FIELDS,
    channel.experts || [],
    cursor,
    render,
  );

  return replaceCollectionFields(
    withExperts,
    CHANNEL_FORMATION_FIELDS,
    channel.formations || [],
    cursor,
    render,
  );
};

/**
 * Consume the channel codes of a block without keeping the result, to know
 * where the next block starts in the experts / formations collections.
 */
export const advanceChannelCursor = (content = "", cursor = {}) => {
  replaceChannelFields(content, false, cursor);
  return cursor;
};

export const replaceUnreferencedBlock = (
  content = "",
  escapeJsonChars = false,
  cursor = {},
) => {
  const fiduciaryLogo = store.getState().auth.currentClient.avatarUrl;
  const nlNewsletter =
    store.getState().currentCampaign.uaNewsletter.numCampaign;
  const introduction = store.getState().currentCampaign.email.introduction;
  const scheduledDeliveryAt =
    store.getState().currentCampaign.email.scheduledDeliveryAt;
  const nlNextNewsletter = getNextNewsletterNum(nlNewsletter);
  const lastSentDate = store.getState().currentCampaign.email.lastSentDate
    ? moment(store.getState().currentCampaign.email.lastSentDate)
    : null;

  const nlPeriodMonth = store.getState().currentCampaign.email?.nlPeriodMonth;
  const nlPeriodYear = store.getState().currentCampaign.email?.nlPeriodYear;
  const campaignLanguage =
    store.getState().currentCampaign.email?.language || "fr";

  const sendMode = store.getState().currentCampaign.email?.sendMode;
  const daysToAdd = sendMode === "TWO_WEEKS" ? 28 : 14;

  content = replaceChannelFields(content, escapeJsonChars, cursor);

  // Every code is replaced globally: a block may hold the same code twice.
  const replaceCode = (acc, code, raw) => {
    const value = raw === undefined || raw === null ? "" : String(raw);
    const rendered = escapeJsonChars ? escapeJsonSpecialChars(value) : value;
    return acc.replace(new RegExp(code, "g"), () => rendered);
  };

  return Object.keys(UNREFERENCED_BLOCK_CODES).reduce((acc, code) => {
    if (code === "FIDUCIARY_LOGO") {
      acc = replaceCode(acc, code, fiduciaryLogo);
    } else if (code === "NL_NEWSLETTER") {
      acc = replaceCode(acc, code, nlNewsletter);
    } else if (code === "NL_NEXT_NEWSLETTER") {
      acc = replaceCode(acc, code, nlNextNewsletter || "");
    } else if (code === "INTRODUCTION_NL") {
      acc = replaceCode(acc, code, introduction);
    } else if (code === "NL_DATE_DMY") {
      acc = replaceCode(
        acc,
        code,
        UNREFERENCED_BLOCK_CODES["NL_DATE_DMY"](scheduledDeliveryAt || null),
      );
    } else if (code === "NL_PERIOD_MY") {
      const periodDate =
        nlPeriodMonth && nlPeriodYear
          ? moment({
              year: Number(nlPeriodYear),
              month: Number(nlPeriodMonth) - 1,
              day: 1,
            })
          : null;
      acc = replaceCode(
        acc,
        code,
        UNREFERENCED_BLOCK_CODES["NL_PERIOD_MY"](
          periodDate,
          String(campaignLanguage).toLowerCase(),
        ),
      );
    } else if (code === "NL_NEXT_DATE_DMY") {
      const nextDate = lastSentDate
        ? lastSentDate.clone().add(daysToAdd, "days")
        : moment().add(daysToAdd, "days");
      acc = replaceCode(
        acc,
        code,
        UNREFERENCED_BLOCK_CODES["NL_NEXT_DATE_DMY"](nextDate),
      );
    } else {
      acc = replaceCode(acc, code, UNREFERENCED_BLOCK_CODES[code]());
    }
    return acc;
  }, content + "");
};

export const assembleNewsletter = (template, blocks, callback) => {
  let generatedBlocks = extractBlocksDesign(blocks);

  const design = JSON.parse(template.design);
  let baseTemplate = {
    ...design,
    body: { ...design.body, rows: generatedBlocks },
  };
  callback(baseTemplate);
};

/**
 * Get blocks design with feeds replacement
 */
export const extractBlocksDesign = (blocks) => {
  const cursor = {};

  return blocks.reduce((generatedBlocks, block) => {
    let design;
    if (
      TEMPLATE_TYPES["UNREFERENCED_BLOCK"] == block.type ||
      block.feeds?.length > 0
    ) {
      try {
        if (TEMPLATE_TYPES["UNREFERENCED_BLOCK"] !== block.type) {
          design = replaceContentFeed(block.design, block.feeds, true);

          design = JSON.parse(design);
          design.columns = design.columns.map((column) => {
            let col = JSON.stringify(column);
            let regex = new RegExp(
              "(" +
                ALL_LAYOUT_STRING_CODES.map(({ key }) => key).join("|") +
                ")",
              "g",
            );
            let s = (col.match(regex) || []).length;
            if (s > 0) {
              return EMPTY_COLUMN_EXAMPLE;
            }
            return column;
          });
        } else {
          let content = replaceUnreferencedBlock(block.design, true, cursor);
          design = JSON.parse(content);
        }
      } catch (ex) {
        // A block that cannot be rebuilt is dropped from the newsletter,
        // so name it: a silent hole is otherwise very hard to trace back.
        console.error(`Unable to build block ${block.id} (${block.type})`, ex);
      }
    }
    return design ? generatedBlocks.concat(design) : generatedBlocks;
  }, []);
};

/**
 * Build new template (design & html) from existed template & blocks
 */
export const constructTemplateFromBlocks = (blocks, template) => {
  const $ = load(template.content);

  $("body > table > tbody > tr > td").html(
    blocks
      .map(({ content }) => {
        return content;
      })
      .join(""),
  );

  const rows = blocks.map(({ design }) =>
    JSON.parse(removeDynamicFieldsIndexes(design)),
  );

  let design = JSON.parse(removeDynamicFieldsIndexes(template.design));
  design.body.rows = rows;
  return {
    content: removeDynamicFieldsIndexes($.html()),
    design,
  };
};
